import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Icon from '../AppIcon';
import Image from '../AppImage';
import UserAccountDropdown from './UserAccountDropdown';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar, { useSidebar } from './Sidebar';

const NavigationBar = ({ children, stats }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, isAdmin } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { isCollapsed } = useSidebar();

  useEffect(() => {
    document.body?.classList?.add('dashboard-surface');
    return () => document.body?.classList?.remove('dashboard-surface');
  }, []);

  const isImpersonating = Boolean(sessionStorage?.getItem('superAdminImpersonating'));
  const isSuperAdmin = isAdmin && !isImpersonating;

  const navigationItems = [
    {
      label: 'Home',
      path: '/home-page',
      icon: 'Home',
      tooltip: 'Home page'
    },
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Manage your forms'
    },
    {
      label: 'Templates',
      path: '/templates-gallery',
      icon: 'LayoutTemplate',
      tooltip: 'Browse form templates'
    },
    {
      label: 'Builder',
      path: '/form-builder',
      icon: 'Wrench',
      tooltip: 'Create and edit forms'
    },
    {
      label: 'Analytics',
      path: '/analytics',
      icon: 'BarChart3',
      tooltip: 'View form performance'
    },
    {
      label: 'Submissions',
      path: '/submissions-management',
      icon: 'Inbox',
      tooltip: 'Manage all submissions'
    },
    {
      label: 'A/B Testing',
      path: '/a-b-testing-dashboard',
      icon: 'TestTube2',
      tooltip: 'Test form variations'
    },
    {
      label: 'Embed',
      path: '/embed-settings',
      icon: 'Code2',
      tooltip: 'Embed forms on your site'
    },
    {
      label: 'Support',
      path: '/help-center-hub',
      icon: 'Headphones',
      tooltip: 'Help and support center'
    },
    {
      label: 'API Docs',
      path: '/api-reference',
      icon: 'BookOpen',
      tooltip: 'Developer API reference'
    },
    ...(isSuperAdmin ? [
      { label: 'Super Admin', path: '/super-admin-dashboard', icon: 'ShieldCheck', tooltip: 'Super admin dashboard' },
      { label: 'Client Accounts', path: '/account-management-console', icon: 'Users', tooltip: 'Manage client accounts' },
      { label: 'Pricing Plans', path: '/pricing-plans-management', icon: 'Tag', tooltip: 'Manage pricing plans' },
      { label: 'Subscriptions', path: '/subscription-management-dashboard', icon: 'CreditCard', tooltip: 'Manage subscriptions' },
      { label: 'User Analytics', path: '/user-analytics-dashboard', icon: 'BarChart3', tooltip: 'View product analytics' },
      { label: 'A2P Consents', path: '/a2p-consent-management', icon: 'ShieldCheck', tooltip: 'Manage A2P consents' },
      { label: 'Payment Processors', path: '/payment-processors-management', icon: 'Zap', tooltip: 'Manage payment processors' },
    ] : []),
  ];

  const isActive = (path) => (
    path === '/templates-gallery'
      ? location?.pathname === path || location?.pathname?.startsWith('/templates/')
      : location?.pathname === path
  );

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      closeMobileMenu();
      toast?.success('Logged out successfully');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast?.error('Failed to logout. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-[1000] transition-smooth">
        <div className="h-full px-4 lg:px-6 flex items-center justify-between gap-4">
          {/* Logo - responsive width */}
          <Link 
            to="/dashboard" 
            className="flex-shrink-0 flex items-center gap-3 transition-smooth hover:opacity-80"
            onClick={closeMobileMenu}
          >
            <div className="w-32 sm:w-40 lg:w-48 h-auto rounded-md flex items-center justify-center transition-smooth overflow-hidden">
              <Image src="/assets/images/web-forms-logo-1769800970615.png" alt="WebForms.biz logo" className="w-full h-auto object-contain" />
            </div>
          </Link>

          {/* Minimized Stats - hidden on mobile, visible on desktop */}
          {stats && stats?.length > 0 && (
            <div className="hidden xl:flex items-center gap-4 flex-1 justify-center max-w-3xl">
              {stats?.map((stat, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between gap-3 px-4 py-2 bg-muted/50 rounded-md border border-border/50 hover:bg-muted transition-smooth min-w-[180px]"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded bg-primary/10">
                      <Icon 
                        name={stat?.icon} 
                        size={16} 
                        color="var(--color-primary)" 
                      />
                    </div>
                    <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{stat?.label}</span>
                  </div>
                  <span className="text-lg font-bold text-foreground">{stat?.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Right side - User dropdown and mobile menu */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Icon 
                name={isDarkMode ? 'Sun' : 'Moon'} 
                size={20} 
                color="var(--color-foreground)" 
              />
            </button>
            
            <UserAccountDropdown />
            
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={mobileMenuOpen ? 'X' : 'Menu'} 
                size={24} 
                color="var(--color-foreground)" 
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-background z-[1020] lg:hidden animate-fade-in"
          style={{ top: '64px' }}
        >
          <div className="h-full overflow-y-auto scrollbar-custom">
            <div className="p-6 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMobileMenu}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md font-medium text-base
                    transition-smooth
                    ${isActive(item?.path)
                      ? 'text-primary bg-primary/10' : 'text-[#6B7280] hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon
                    name={item?.icon}
                    size={20}
                    color={isActive(item?.path) ? 'var(--color-primary)' : '#107570'}
                  />
                  <span>{item?.label}</span>
                </Link>
              ))}

              <div className="pt-4 mt-4 border-t border-border space-y-2">
                <Link
                  to="/account-settings"
                  onClick={closeMobileMenu}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md font-medium text-base
                    transition-smooth
                    ${isActive('/account-settings')
                      ? 'text-primary bg-primary/10' : 'text-[#6B7280] hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name="Settings" size={20} color={isActive('/account-settings') ? 'var(--color-primary)' : '#107570'} />
                  <span>Settings</span>
                </Link>
                <button
                  onClick={handleMobileLogout}
                  disabled={isLoggingOut}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium text-base text-destructive hover:bg-destructive/10 transition-smooth disabled:opacity-50"
                >
                  <Icon name={isLoggingOut ? "Loader2" : "LogOut"} size={20} className={isLoggingOut ? "animate-spin" : ""} />
                  <span>{isLoggingOut ? 'Logging out...' : 'Log Out'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="h-16" />
      {/* Main content wrapper with sidebar spacing */}
      <div className="transition-all duration-300 ease-in-out lg:ml-[var(--sidebar-width)]">
        {children}
      </div>
    </>
  );
};

export default NavigationBar;